import type { OwnerParticipant, TimetableGroup } from "@teamna/shared";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useNewParticipants } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import {
  useDecidePendingMember,
  useParticipants,
  usePendingMembers,
  usePlaceParticipant,
  useTimetable,
  type PendingMember,
} from "@teamna/web-client/hooks/use-setup.ts";
import {
  Avatar,
  Button,
  DataTable,
  ErrorState,
  InlineError,
  Select,
  SkeletonList,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { DecideSessionsList } from "../decide-sessions.tsx";
import { phoneText } from "../families/model.ts";
import { LinkProgressBar } from "../link-progress.tsx";
import { ClaimRow, claimPairs, useClaimActions } from "../new-participants-ask.tsx";
import { describeWeekdays } from "../setup/frame.tsx";
import { StaleNotice } from "./stale-notice.tsx";

/**
 * What each row of the To do list shows when it opens, in place on Home. Most panels do the whole
 * job there, with the same actions as the page the job comes from, and a way to that page. The
 * "no parent yet" panel shows the bar and opens New participants, where the linking tools are.
 */

/** "See all on New swimmers": the page where the job lives in full. */
function PageLink({ to, children }: { to: string; children: string }) {
  return (
    <Link to={to} className="self-start text-[13.5px] font-semibold text-accent-strong underline-offset-2 hover:underline">
      {children}
    </Link>
  );
}

/** Parents who answered the find link: Link or No on each answer, as on New participants. */
export function AnswersPanel({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const list = useNewParticipants(businessId);
  const actions = useClaimActions(businessId, list.data?.waiting ?? []);
  if (!list.data) return list.isLoading ? <SkeletonList rows={2} /> : <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const pairs = claimPairs(list.data.claims);
  const open = list.data.claims.filter((claim) => claim.status === "new");
  const clean = open.filter((claim) => claim.pendingIds.length > 0 && !pairs.isClash(claim));
  const parents = label("member", { plural: true });
  return (
    <div className="flex flex-col gap-3">
      {list.isError ? <StaleNotice onRetry={() => void list.refetch()} /> : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13.5px] text-ink-2">
          Nothing links until you tap Link. A mother and a father on one {label("participant")} go in one family. Any other mix of {parents} on one {label("participant")} is a clash.
        </p>
        {clean.length > 1 ? (
          <Button size="sm" variant="secondary" loading={actions.busy} onClick={() => void actions.linkMany(clean, pairs)}>
            Link all without a clash
          </Button>
        ) : null}
      </div>
      {open.length ? (
        <ul aria-label="Answers to check" className="overflow-hidden rounded-card border border-line bg-paper [&>li:first-child]:border-t-0">
          {open.map((claim) => (
            <ClaimRow key={claim.id} claim={claim} pairs={pairs} actions={actions} />
          ))}
        </ul>
      ) : (
        <p className="text-[13.5px] text-ink-2">All answers checked.</p>
      )}
      <PageLink to="/owner/new-participants">All answers and the group message</PageLink>
    </div>
  );
}

/** Names coaches added that still have no parent: the bar, and the way to the page that links them. */
export function LinkParentsPanel({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const navigate = useNavigate();
  const list = useNewParticipants(businessId);
  if (!list.data) return list.isLoading ? <SkeletonList rows={1} /> : <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const { linked, waiting } = list.data.progress;
  const coaches = [...new Set(list.data.waiting.flatMap((w) => w.coachNames))];
  const participants = label("participant", { plural: true });
  return (
    <div className="flex flex-col gap-3">
      {list.isError ? <StaleNotice onRetry={() => void list.refetch()} /> : null}
      <p className="text-[13.5px] text-ink-2">
        {linked} of {linked + waiting} linked to a {label("member")}
        {coaches.length ? `. Added by ${coaches.length === 1 ? coaches[0] : `${coaches.length} ${label("staff", { plural: true })}`}` : ""}.
      </p>
      <LinkProgressBar
        linked={linked}
        waiting={waiting}
        check={new Set(list.data.claims.filter((c) => c.status === "new").flatMap((c) => c.pendingIds)).size}
        participants={participants}
        member={label("member")}
      />
      <div>
        <Button onClick={() => void navigate({ to: "/owner/new-participants" })}>Link {label("member", { plural: true })}</Button>
      </div>
    </div>
  );
}

/**
 * The next month has no sessions (spec `teamna/strategy/timetable-months.md`): the Timetable opens on
 * that month, where the owner copies the month before or starts empty.
 */
export function PlanMonthPanel({ month }: { month: string | null }) {
  const navigate = useNavigate();
  const name = month ? new Date(`${month}-01T00:00:00Z`).toLocaleString("en-GB", { month: "long", timeZone: "UTC" }) : "the next month";
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13.5px] text-ink-2">Start {name} from a copy of the month before, then check the holidays and publish.</p>
      <div>
        <Button onClick={() => void navigate({ to: "/owner/schedule", search: { view: "timetable", ...(month ? { month } : {}) } as never })}>
          Plan {name}
        </Button>
      </div>
    </div>
  );
}

/** Someone came and was not booked, before 2026-10-29: Take 1 session or Let it go, on each visit. Came takes the session by itself since. */
export function SessionsPanel({ businessId, timezone }: { businessId: string; timezone: string }) {
  return <DecideSessionsList businessId={businessId} timezone={timezone} />;
}

function queueColumns({
  label,
  changing,
  groups,
  confirm,
  setChanging,
  stale,
}: {
  label: ReturnType<typeof useLabels>["label"];
  changing: string | null;
  groups: TimetableGroup[];
  confirm: (row: OwnerParticipant, groupId: string | null) => void;
  setChanging: (id: string | null) => void;
  /**
   * The list is being read again, or that read failed: the rows can be old. Confirm writes the group of the row with
   * no check on the server, so an old row could undo another owner's change. Off until a good read
   * (review F6.1).
   */
  stale: boolean;
}): DataTableColumn<OwnerParticipant>[] {
  return [
    {
      id: "name",
      header: label("participant", { capital: true }),
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar name={row.displayName} photoUrl={row.photoUrl} size={32} />
          <div className="min-w-0">
            <p className="font-semibold text-ink">
              <span dir="auto">{row.displayName}</span>
              {row.age !== null ? <span className="font-normal text-ink-3">, {row.age}</span> : null}
            </p>
            <p className="text-[13px] text-ink-3">{row.members.map((member) => member.name).join(" and ")}</p>
          </div>
        </div>
      ),
    },
    {
      id: "pick",
      header: `${label("group", { capital: true })} picked`,
      // The level shows in its own column with Change next to it. With no level (or "Not sure") the
      // dropdown shows at once, so the owner picks there. A pick confirms the row with that level.
      cell: (row) =>
        changing === row.id || !row.groupId ? (
          <div className="flex items-center gap-2">
            <Select
              label={`${label("group", { capital: true })} for ${row.displayName}`}
              className="w-full max-w-[220px]"
              defaultValue={row.groupId ?? ""}
              disabled={stale}
              onChange={(event) => {
                if (event.target.value) confirm(row, event.target.value);
              }}
            >
              <option value="" disabled>
                {row.groupId ? "Choose" : `Choose a ${label("group")}`}
              </option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
            {row.groupId ? (
              <Button size="sm" variant="ghost" onClick={() => setChanging(null)}>
                Cancel
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span dir="auto">{row.groupName ?? "Not sure"}</span>
            <Button
              size="sm"
              variant="ghost"
              disabled={stale}
              onClick={() => setChanging(row.id)}
              aria-label={`Change ${label("group")} for ${row.displayName}`}
            >
              Change
            </Button>
          </div>
        ),
    },
    { id: "days", header: "Days", hideOnPhone: true, cell: (row) => describeWeekdays(row.usualWeekdays) },
    {
      id: "actions",
      header: "",
      align: "end",
      cell: (row) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            disabled={stale}
            onClick={() => confirm(row, row.groupId)}
            aria-label={`Confirm ${row.displayName}`}
          >
            Confirm
          </Button>
        </div>
      ),
    },
  ];
}

/** Participants a member added: Confirm with the picked group, or change the group in its column first. */
export function ConfirmParticipantsPanel({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const participants = useParticipants(businessId);
  const timetable = useTimetable(businessId);
  const place = usePlaceParticipant(businessId);
  const [changing, setChanging] = useState<string | null>(null);

  // The same rule as the API's To do source: added by a member, not confirmed yet.
  const queue = (participants.data ?? []).filter((row) => row.createdBy === "member" && !row.confirmedAt);
  const groups = (timetable.data?.groups ?? []).filter((group) => group.kind === "group" && !group.openToAll);

  function confirm(row: OwnerParticipant, groupId: string | null) {
    place.mutate(groupId ? { id: row.id, groupId, confirm: true } : { id: row.id, confirm: true }, {
      onSuccess: () => {
        setChanging(null);
        showToast(`${row.displayName} confirmed`);
      },
    });
  }

  async function confirmAll() {
    const results = await Promise.allSettled(
      queue.map((row) =>
        place.mutateAsync(
          row.groupId ? { id: row.id, groupId: row.groupId, confirm: true } : { id: row.id, confirm: true },
        ),
      ),
    );
    // A failure shows under the table; the rows that saved stay saved.
    const done = results.filter((result) => result.status === "fulfilled").length;
    if (done > 0) showToast(`${done} confirmed`);
  }

  const title = `New ${label("participant", { plural: true })} to confirm`;
  // Off while the list is read again, and after a reload that failed: until a good read, a row
  // can be old (review F6.1, F7.1).
  const stale = participants.isError || participants.isFetching;
  // A stale list closes the level picker for good, so it does not come back by itself after the
  // read (review F8.1). Set during render, React's way to adjust state to new data.
  if (stale && changing !== null) setChanging(null);
  const columns = queueColumns({ label, changing, groups, confirm, setChanging, stale });

  if (participants.error && !participants.data) return <ErrorState error={participants.error} onRetry={() => void participants.refetch()} />;
  return (
    <div className="flex flex-col gap-3">
      {participants.isError ? <StaleNotice onRetry={() => void participants.refetch()} /> : null}
      {queue.length > 1 ? (
        <div>
          <Button variant="secondary" size="sm" disabled={stale} loading={place.isPending} onClick={() => void confirmAll()}>
            Confirm all
          </Button>
        </div>
      ) : null}
      <DataTable
        caption={title}
        columns={columns}
        rows={queue}
        rowKey={(row) => row.id}
        isLoading={participants.isLoading}
        empty={{
          title: "Nobody to confirm",
          message: `When a ${label("member")} adds a ${label("participant")}, they show here for you to confirm.`,
        }}
        countLabel={(count) => `${count} to confirm`}
      />
      {place.error ? <InlineError error={place.error} /> : null}
    </div>
  );
}

function pendingColumns({
  act,
  busy,
}: {
  act: (row: PendingMember, decision: "approve" | "decline") => void;
  /** A decision is on its way: no second one until it comes back (review F2.2). */
  busy: boolean;
}): DataTableColumn<PendingMember>[] {
  return [
    { id: "name", header: "Name", cell: (row) => <span className="font-semibold text-ink">{row.name}</span> },
    { id: "phone", header: "Mobile", cell: (row) => (row.phoneE164 ? phoneText(row.phoneE164) : row.phoneMasked), hideOnPhone: true },
    {
      id: "actions",
      header: "",
      align: "end",
      cell: (row) => (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" disabled={busy} onClick={() => act(row, "approve")} aria-label={`Approve ${row.name}`}>
            Approve
          </Button>
          <Button variant="danger" disabled={busy} onClick={() => act(row, "decline")} aria-label={`Decline ${row.name}`}>
            Decline
          </Button>
        </div>
      ),
    },
  ];
}

/** People who joined with the code and are not on the list: Approve or Decline. */
export function PendingMembersPanel({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const pending = usePendingMembers(businessId);
  const decide = useDecidePendingMember(businessId);
  const rows = pending.data ?? [];
  const title = `Pending ${label("member", { plural: true })}`;

  function act(row: PendingMember, decision: "approve" | "decline") {
    decide.mutate(
      { id: row.id, decision },
      {
        onSuccess: () => showToast(decision === "approve" ? `${row.name} approved` : `${row.name} declined`),
      },
    );
  }

  if (pending.error && !pending.data) return <ErrorState error={pending.error} onRetry={() => void pending.refetch()} />;
  return (
    <div className="flex flex-col gap-3">
      {pending.isError ? <StaleNotice onRetry={() => void pending.refetch()} /> : null}
      <DataTable
        caption={title}
        columns={pendingColumns({ act, busy: decide.isPending })}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={pending.isLoading}
        empty={{
          title: "Nobody is waiting",
          message: `${label("member", { plural: true, capital: true })} on your list get in at once.`,
        }}
        countLabel={(count) => `${count} waiting`}
      />
      {decide.error ? <InlineError error={decide.error} /> : null}
    </div>
  );
}
